export const TAU = Math.PI * 2;

export const clamp = (v: number, a: number, b: number) =>
  Math.max(a, Math.min(b, v));

export const lerp = (a: number, b: number, u: number) => a + (b - a) * u;

export const lerp3 = (
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  u: number,
): [number, number, number] => [lerp(a[0], b[0], u), lerp(a[1], b[1], u), lerp(a[2], b[2], u)];

export const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

export const hash = (n: number) => {
  const s = Math.sin(n * 127.1) * 43758.5453;
  return s - Math.floor(s);
};

export const noise2 = (x: number, y: number) => {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hash(ix * 19.1 + iy * 47.3);
  const b = hash((ix + 1) * 19.1 + iy * 47.3);
  const c = hash(ix * 19.1 + (iy + 1) * 47.3);
  const d = hash((ix + 1) * 19.1 + (iy + 1) * 47.3);
  return lerp(lerp(a, b, ux), lerp(c, d, ux), uy) * 2 - 1;
};

export const curl = (x: number, y: number, time: number) => {
  const e = 0.015;
  return {
    x: (noise2(x, y + e + time) - noise2(x, y - e + time)) / (2 * e),
    y: -(noise2(x + e + time * 0.7, y) - noise2(x - e + time * 0.7, y)) / (2 * e),
  };
};

export type Vec2 = { x: number; y: number };

export const rot = (x: number, y: number, a: number): Vec2 => ({
  x: x * Math.cos(a) + y * Math.sin(a),
  y: -x * Math.sin(a) + y * Math.cos(a),
});

export const lerpPt = (a: Vec2, b: Vec2, u: number): Vec2 => ({
  x: lerp(a.x, b.x, u),
  y: lerp(a.y, b.y, u),
});
